/**
 * A SEATED peninsula's top as one slab.
 *
 * A peninsula arm people sit at used to be topped by three pieces: the run's
 * 26" slab over its cabinets (and the back panel), a separate 12" knee strip
 * outboard of the panel, and, at the arm's root, the crossing run's slab over
 * the corner box. Three things followed from the split, each reported on a real
 * kitchen (Stocks / Scan 1, 2026-10-21): the waterfall at the free end spanned
 * only the 26" piece (the strip stands on no cabinet, so it has no leg); the
 * crossing slab stopped at the cabinet face, leaving the panel's inch bare
 * beside the strip; and the slab layout cut the peninsula as two long strips
 * with a seam down its length.
 *
 * So the arm's top is ONE slab from its free end to the face it meets at the
 * root: cook-side overhang, cabinets, back panel and knee overhang, full depth.
 * Whatever crosses into that rectangle stops at its edge — the crossing run's
 * slab butts against the peninsula's cook-side edge, the joint a fabricator
 * cuts at an L. Only a RANGE breaks it (a range carries no slab): the arm is
 * cut either side, and the bar behind the range is its own piece, panel plus
 * knee, joined to both.
 *
 * Pure. Called twice with the same inputs: by the generator once it has
 * planned the seating (`materializeCandidate`), and by the run engine when a
 * run whose arm carries `seating` is re-solved (`fillRun`) — so the first edit
 * of a generated kitchen rebuilds the same slab instead of dropping the
 * overhang. Safe to run on its own output.
 */

const { KITCHEN_COUNTERTOP_THICKNESS } = require('./constants');
const { getWallAxes } = require('./wallMath');
const { objectFootprint } = require('../geometry/objectFootprint');
const { waterfallEnds } = require('../cabinet-runs/countertopEnds');

const IN = 0.0254;
/** The arm's back panel — the inch outboard of its line every slab covers. */
const PANEL = 1 * IN;
/** 5 mm: the pieces here are all built from the same numbers. */
const EPS = 0.005;
/** How close to parallel / perpendicular two rotations must be. */
const AXIS_EPS = 1e-3;

/** The arm's own frame: `a` along its line from its start, `n` into the room. */
function armFrame(arm) {
  const axes = getWallAxes(arm);
  if (!axes) return null;
  const { start, wallDir: d, wallNormal: m } = axes;
  return {
    axes,
    toArm: (p) => ({
      a: (p.x - start.x) * d.x + (p.z - start.z) * d.z,
      n: (p.x - start.x) * m.x + (p.z - start.z) * m.z,
    }),
    toWorld: (a, n) => ({
      x: start.x + a * d.x + n * m.x,
      z: start.z + a * d.z + n * m.z,
    }),
  };
}

/** A plain rectangular countertop (a kite's outline is its own shape). */
const isPlainTop = (o) =>
  o?.type === 'countertop' &&
  !Array.isArray(o.polygonCorners) &&
  !!o.center &&
  o.dimensions?.x > 0 &&
  o.dimensions?.z > 0;

/** A top's rectangle in the arm frame — exact for one square to the arm. */
function boxOf(o, frame) {
  const fp = objectFootprint(o);
  if (!fp) return null;
  const pts = fp.map(frame.toArm);
  const as = pts.map((p) => p.a);
  const ns = pts.map((p) => p.n);
  return {
    a0: Math.min(...as),
    a1: Math.max(...as),
    n0: Math.min(...ns),
    n1: Math.max(...ns),
  };
}

const turnFrom = (o, frame) => (o.rotation?.y ?? 0) - frame.axes.wallRotationY;
const isParallel = (o, frame) =>
  Math.abs(Math.sin(turnFrom(o, frame))) < AXIS_EPS;
const isSquareTo = (o, frame) =>
  Math.abs(Math.cos(turnFrom(o, frame))) < AXIS_EPS;
const overlap = (a0, a1, b0, b1) => Math.min(a1, b1) - Math.max(a0, b0);

/**
 * The arm's own counter: tops parallel to it that stand on its cabinet side
 * and reach back to its line — the run's slab (which covers the panel too) or
 * an already-seated one. The panel bridge and an old knee strip stand wholly
 * outboard, and a top across the room stands nowhere near the line.
 */
function isArmTop(box, outerN) {
  return box.n1 > EPS && box.n0 <= EPS && box.n0 >= outerN - EPS;
}

/** `[lo, hi]` less every break, in order. */
function intervalsBetween(lo, hi, breaks) {
  let out = [{ a0: lo, a1: hi }];
  for (const b of breaks) {
    out = out.flatMap((iv) => {
      if (overlap(iv.a0, iv.a1, b.start, b.end) <= EPS) return [iv];
      return [
        { a0: iv.a0, a1: Math.min(iv.a1, b.start) },
        { a0: Math.max(iv.a0, b.end), a1: iv.a1 },
      ].filter((p) => p.a1 - p.a0 > EPS);
    });
  }
  return out;
}

/**
 * A slab that runs THROUGH the arm's band — a wall run the arm lands on
 * mid-span — keeps running; the seated slab stops at its edge instead.
 */
function stopAtThroughTops(intervals, throughs) {
  let out = intervals;
  for (const t of throughs) {
    out = out.flatMap((iv) => {
      if (overlap(iv.a0, iv.a1, t.a0, t.a1) <= EPS) return [iv];
      return [
        { a0: iv.a0, a1: Math.min(iv.a1, t.a0) },
        { a0: Math.max(iv.a0, t.a1), a1: iv.a1 },
      ].filter((p) => p.a1 - p.a0 > EPS);
    });
  }
  return out;
}

/** A rectangle in the arm frame as a countertop's geometry. */
function rectGeometry(frame, a0, a1, n0, n1, y) {
  const c = frame.toWorld((a0 + a1) / 2, (n0 + n1) / 2);
  return {
    center: { x: c.x, y, z: c.z },
    rotation: { x: 0, y: frame.axes.wallRotationY, z: 0 },
    length: a1 - a0,
    depth: n1 - n0,
  };
}

/** Every parent of `tops`, each once, in order. */
function parentsOf(tops) {
  const seen = new Set();
  const out = [];
  for (const t of tops) {
    for (const p of t.parents || []) {
      if (!p?.id || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
  }
  return out;
}

/** The arm top `box` with its along-arm extent cut to `[a0, a1]`. */
function cropAlong(top, box, frame, a0, a1, id) {
  const g = rectGeometry(frame, a0, a1, box.n0, box.n1, top.center.y);
  return {
    ...top,
    id,
    center: g.center,
    dimensions: { ...top.dimensions, x: g.length },
  };
}

/**
 * A top square to the arm with its extent along the arm's NORMAL cut to
 * `[n0, n1]` — its own length, since its own axis runs that way.
 */
function cropAcross(top, box, frame, n0, n1) {
  const shift = (n0 + n1) / 2 - (box.n0 + box.n1) / 2;
  const m = frame.axes.wallNormal;
  return {
    ...top,
    center: {
      ...top.center,
      x: top.center.x + shift * m.x,
      z: top.center.z + shift * m.z,
    },
    dimensions: { ...top.dimensions, x: n1 - n0 },
  };
}

/**
 * The tops that cross into the seated slabs, settled: one that dies under a
 * slab is cut back to its edge; one wholly inside it (a corner square) is
 * gone, the slab covering it. Returns the replacement for each top touched,
 * by id (`null` = removed), and, per slab, the tops it took over — whose
 * cabinets it now stands on.
 */
function settleCrossingTops(others, slabs, frame, outerN, frontN) {
  const replaced = new Map();
  const takenBy = slabs.map(() => []);
  for (const { top, box } of others) {
    if (!isSquareTo(top, frame)) continue;
    if (overlap(box.n0, box.n1, outerN, frontN) <= EPS) continue;
    const hit = slabs
      .map((s, i) => ({ i, len: overlap(box.a0, box.a1, s.a0, s.a1) }))
      .filter((h) => h.len > EPS)
      .sort((x, y) => y.len - x.len)[0];
    if (!hit) continue;
    const slab = slabs[hit.i];
    const inBand = box.n0 >= outerN - EPS && box.n1 <= frontN + EPS;
    if (inBand) {
      if (box.a0 < slab.a0 - EPS || box.a1 > slab.a1 + EPS) continue;
      replaced.set(top.id, null);
    } else if (box.n1 > frontN + EPS && box.n0 >= outerN - EPS) {
      replaced.set(top.id, cropAcross(top, box, frame, frontN, box.n1));
    } else if (box.n0 < outerN - EPS && box.n1 <= frontN + EPS) {
      replaced.set(top.id, cropAcross(top, box, frame, box.n0, outerN));
    } else {
      continue;
    }
    takenBy[hit.i].push(top);
  }
  return { replaced, takenBy };
}

/**
 * @param {Object} args
 * @param {Object} args.arm   the peninsula's virtual wall
 * @param {{start:number, end:number, depth:number}} args.seating  the seated
 *   span along the arm (its own params, metres) and the knee overhang
 * @param {Object[]} args.objects  the objects to settle; only countertops are touched
 * @param {Array<{start:number, end:number}>} [args.breaks]  along-arm spans a
 *   range stands in — the slab is cut there and bridged behind it
 * @returns {{ objects: Object[], seated: Object[], bars: Object[] }}
 *   `objects` in place of the input; `seated` the arm's full-depth slabs,
 *   `bars` the pieces behind its ranges. Nothing seated (both empty, input
 *   returned) when the arm has no counter of its own.
 */
function seatPeninsulaTop({ arm, seating, objects, breaks = [] }) {
  const unchanged = { objects, seated: [], bars: [] };
  const frame = armFrame(arm);
  if (!frame || !seating || !(seating.end > seating.start)) return unchanged;
  const outerN = -(PANEL + seating.depth);

  const boxed = objects
    .filter(isPlainTop)
    .map((top) => ({ top, box: boxOf(top, frame) }))
    .filter((t) => t.box);
  const armTops = boxed.filter(
    (t) =>
      isParallel(t.top, frame) &&
      isArmTop(t.box, outerN) &&
      overlap(t.box.a0, t.box.a1, seating.start, seating.end) > EPS
  );
  if (!armTops.length) return unchanged;
  const armIds = new Set(armTops.map((t) => t.top.id));
  // Behind a range the seating band is only the panel and the knee — a
  // panel bridge or an old knee strip standing there is replaced by the bar.
  const outboard = boxed.filter(
    (t) =>
      !armIds.has(t.top.id) &&
      isParallel(t.top, frame) &&
      t.box.n1 <= EPS &&
      t.box.n0 >= outerN - EPS &&
      overlap(t.box.a0, t.box.a1, seating.start, seating.end) > EPS
  );
  const outboardIds = new Set(outboard.map((t) => t.top.id));
  const frontN = Math.max(...armTops.map((t) => t.box.n1));
  const others = boxed.filter(
    (t) => !armIds.has(t.top.id) && !outboardIds.has(t.top.id)
  );

  const sortedBreaks = [...breaks]
    .filter((b) => overlap(b.start, b.end, seating.start, seating.end) > EPS)
    .sort((a, b) => a.start - b.start);
  const throughs = others
    .filter(
      (t) =>
        isSquareTo(t.top, frame) &&
        t.box.n0 < outerN - EPS &&
        t.box.n1 > frontN + EPS
    )
    .map((t) => t.box);
  const spans = stopAtThroughTops(
    intervalsBetween(seating.start, seating.end, sortedBreaks),
    throughs
  );

  const thickness = armTops[0].top.dimensions.y ?? KITCHEN_COUNTERTOP_THICKNESS;
  const topY = armTops[0].top.center.y;
  const usedIds = new Set();
  const slabs = spans.map((span) => {
    const under = armTops
      .map((t) => ({
        ...t,
        len: overlap(t.box.a0, t.box.a1, span.a0, span.a1),
      }))
      .filter((t) => t.len > EPS)
      .sort((x, y) => y.len - x.len);
    const lead = under[0]?.top || armTops[0].top;
    const id =
      under[0] && !usedIds.has(lead.id)
        ? lead.id
        : `${lead.id}_${Math.round(span.a0 * 1000)}`;
    usedIds.add(id);
    const g = rectGeometry(frame, span.a0, span.a1, outerN, frontN, topY);
    return {
      a0: span.a0,
      a1: span.a1,
      top: {
        ...lead,
        id,
        category: {
          ...lead.category,
          countertop: true,
          peninsula: true,
          seatingOverhang: true,
        },
        // What the knee strip told its readers, stated on the slab: the
        // diners sit past its BACK edge (local −Z), this far out.
        seating: { side: 'back', depth: seating.depth },
        center: g.center,
        dimensions: {
          ...lead.dimensions,
          x: g.length,
          y: thickness,
          z: g.depth,
        },
        rotation: g.rotation,
        // Named by its arm, as every peninsula finishing piece is (the panel
        // behind it, the knee strip before it): readers find a seated leg's
        // top by its wall, not only through its cabinets.
        parentWallIds: [arm.id],
        parents: parentsOf(under.map((t) => t.top)),
      },
    };
  });

  // The bar behind each range: the panel and the knee, joined to the slabs
  // either side. Parented to their cabinets so the run that re-solves the
  // arm owns it and replaces it, rather than leaving it standing beside its
  // own new copy.
  const bars = sortedBreaks
    .map((b) => {
      const a0 = Math.max(b.start, seating.start);
      const a1 = Math.min(b.end, seating.end);
      if (!(a1 - a0 > EPS)) return null;
      const touching = slabs.filter(
        (s) => Math.abs(s.a1 - a0) < EPS || Math.abs(s.a0 - a1) < EPS
      );
      const lead = touching[0]?.top || armTops[0].top;
      const g = rectGeometry(frame, a0, a1, outerN, 0, topY);
      return {
        ...lead,
        id: `${armTops[0].top.id}_bar_${Math.round(((a0 + a1) / 2) * 1000)}`,
        label: 'Peninsula Bar Behind Range',
        category: { countertop: true, peninsula: true },
        seating: { side: 'back', depth: seating.depth },
        center: g.center,
        dimensions: {
          ...lead.dimensions,
          x: g.length,
          y: thickness,
          z: g.depth,
        },
        rotation: g.rotation,
        parentWallIds: [arm.id],
        parents: parentsOf(touching.map((s) => s.top)),
      };
    })
    .filter(Boolean);

  // What is left of an arm top past the seated span keeps its own depth. Not
  // of a slab this settled before: settling again (a reached end) restates
  // the whole seated top, and a sliver of the old one past the new end would
  // stand there seated.
  const seatedAt = (a0, a1) =>
    slabs.reduce((sum, s) => sum + Math.max(0, overlap(a0, a1, s.a0, s.a1)), 0);
  const remainders = [];
  for (const { top, box } of armTops) {
    if (top.seating) continue;
    const pieces = intervalsBetween(box.a0, box.a1, [
      ...slabs.map((s) => ({ start: s.a0, end: s.a1 })),
      ...sortedBreaks,
    ]);
    for (const p of pieces) {
      if (seatedAt(p.a0, p.a1) > EPS) continue;
      const id = usedIds.has(top.id)
        ? `${top.id}_${Math.round(p.a0 * 1000)}`
        : top.id;
      usedIds.add(id);
      remainders.push(cropAlong(top, box, frame, p.a0, p.a1, id));
    }
  }

  const { replaced: crossing, takenBy } = settleCrossingTops(
    others,
    slabs,
    frame,
    outerN,
    frontN
  );
  // A slab holds up what it covers: a corner square it took over hands it
  // that square's cabinets, and a slab with no arm counter beneath it (the
  // corner box alone, between a range and the root) stands on the crossing
  // run's.
  const seated = slabs.map((s, i) => {
    const taken = takenBy[i];
    if (!taken.length) return s.top;
    const gone = taken.filter((t) => crossing.get(t.id) === null);
    const own = s.top.parents.length ? [s.top] : [];
    const parents = parentsOf([...own, ...(own.length ? gone : taken)]);
    return parents.length === s.top.parents.length
      ? s.top
      : { ...s.top, parents };
  });
  const out = [];
  let placed = false;
  for (const top of objects) {
    if (armIds.has(top?.id) || outboardIds.has(top?.id)) {
      if (!placed) out.push(...seated, ...bars, ...remainders);
      placed = true;
      continue;
    }
    if (crossing.has(top?.id)) {
      const next = crossing.get(top.id);
      if (next) out.push(next);
      continue;
    }
    out.push(top);
  }
  return { objects: out, seated, bars };
}

/** What a seated slab stands on at its ends: the arm's bodies and their panels. */
const END_BODY_TYPES = new Set([
  'lower-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
  'blank-panel',
  'dishwasher',
]);

/**
 * The seated span run past whatever stands at each OPEN end by the slab's own
 * thickness — the reach a waterfall leg needs.
 *
 * The seated top is a peninsula's, so it reads as free-standing, and on a
 * free-standing top the renderer and the estimate both stand a waterfall leg
 * INSIDE the slab's footprint (an island's slab overhangs its cabinets). A
 * seated span ending flush with the last cabinet put the leg's 1.5" through
 * that cabinet, its outer face on the cabinet's end face: two surfaces in one
 * plane, z-fighting (Stocks / Scan 1, 2026-10-21). Ending the slab one leg's
 * thickness past the outermost body — a cabinet, or the end panel a dishwasher
 * at the tip carries — stands the leg outside it, touching it. An end reaches
 * only where the renderer and estimate would stand a leg (`waterfallEnds`):
 * the root against its wall, a range cut and an arm meeting another stay put,
 * and so does an end lying on ANOTHER arm's back line (a floating L's outer
 * corner), which takes no leg and must stop level with that arm's counters.
 *
 * Pure. Returns `seating` itself when no end reaches.
 *
 * @param {Object} args
 * @param {Object} args.arm  the peninsula's virtual wall
 * @param {{start:number, end:number, depth:number}} args.seating
 * @param {Object[]} args.objects  everything placed so far, the slab included
 * @param {Object[]} args.walls    the room's walls, which may cap an end
 */
function reachOpenEnds({ arm, seating, objects, walls }) {
  const frame = armFrame(arm);
  if (!frame || !seating) return seating;
  const outerN = -(PANEL + seating.depth);
  const slabs = objects
    .filter(
      (o) =>
        isPlainTop(o) &&
        o.seating &&
        (o.parentWallIds || []).includes(arm.id) &&
        isParallel(o, frame)
    )
    .map((top) => ({ top, box: boxOf(top, frame) }))
    .filter((t) => t.box && t.box.n0 <= outerN + EPS && t.box.n1 > EPS);
  if (!slabs.length) return seating;
  const first = slabs.reduce((a, b) => (b.box.a0 < a.box.a0 ? b : a));
  const last = slabs.reduce((a, b) => (b.box.a1 > a.box.a1 ? b : a));
  // A slab's local +X is the arm's own direction (`rectGeometry`), so its
  // `left` end is the span's start and its `right` end the span's end.
  const ctx = { objects, walls };
  const openAtStart =
    Math.abs(first.box.a0 - seating.start) < EPS &&
    waterfallEnds(first.top, ctx).left;
  const openAtEnd =
    Math.abs(last.box.a1 - seating.end) < EPS &&
    waterfallEnds(last.top, ctx).right;
  if (!openAtStart && !openAtEnd) return seating;

  // The bodies in the slab's band that reach its ends or stand against them —
  // an end panel stands on the last cabinet's face, which is the slab's end.
  const bodies = objects
    .filter(
      (o) =>
        END_BODY_TYPES.has(o?.type) &&
        o.dimensions?.isUpper !== true &&
        o.center &&
        o.dimensions
    )
    .map((o) => boxOf(o, frame))
    .filter(
      (b) =>
        b &&
        overlap(b.n0, b.n1, outerN, first.box.n1) > EPS &&
        overlap(b.a0, b.a1, seating.start, seating.end) > -EPS
    );
  if (!bodies.length) return seating;
  // From the last body, not from where the seated span happened to stop: a
  // span reaching on to the arm's synthesized end can run past the cabinets
  // over a strip the fill left empty, and a leg stood out there would leave a
  // void between it and the cabinets it closes.
  const lowest = Math.min(...bodies.map((b) => b.a0));
  const highest = Math.max(...bodies.map((b) => b.a1));
  return {
    ...seating,
    start: openAtStart ? lowest - first.top.dimensions.y : seating.start,
    end: openAtEnd ? highest + last.top.dimensions.y : seating.end,
  };
}

/**
 * The run engine's side: every arm of `run` that carries a seated span
 * (`wall.seating`, persisted on its cabinet wall by `fromPhaseB`) gets its top
 * settled over the run's own countertops, broken at the run's solved range
 * bays on that arm.
 *
 * @param {Object} run  with `segments[].wall` resolved
 * @param {Object[]} countertops  the run's countertops
 * @param {Array<{kind:string, segIndex:number, start:number, end:number}>} bays
 * @returns {Object[]}
 */
function seatRunArms(run, countertops, bays) {
  let out = countertops;
  (run?.segments || []).forEach((seg, segIndex) => {
    const arm = seg?.wall;
    if (!arm?.isVirtual || !arm.seating) return;
    const breaks = (bays || []).filter(
      (b) => b.kind === 'range' && b.segIndex === segIndex
    );
    out = seatPeninsulaTop({
      arm,
      seating: arm.seating,
      objects: out,
      breaks,
    }).objects;
  });
  return out;
}

module.exports = { seatPeninsulaTop, seatRunArms, reachOpenEnds };
